import React, { useCallback, useState } from 'react';
import { I18n } from 'react-redux-i18n';

import { ErrorMessage } from '@common/components/error-message';
import { MessageProps } from '@common/components/MessageBox.types';
import { InfoMessage } from '@common/components/info-message';

export type MessageShowParams = Omit<MessageProps, 'show'>;
export type MessageConfirmParams = Omit<MessageProps, 'show'>;

export const useMessage = () => {
  const [messageProps, setMessageProps] = useState<MessageProps>();
  
  const updateMessage = useCallback((props: MessageProps) => {
    setMessageProps(() => ({
      confirmText: "Ok",
      cancelText: "",
      size: "small",
      ...props,
    }));
  }, []);

  const closeMessage = useCallback(() => {
    updateMessage({ show: false });
  }, [updateMessage]);

  const showMessage = useCallback((config: MessageShowParams) => {
    updateMessage({
      onCustomOk: closeMessage,
      ...config,
      show: true,
    });
  }, [updateMessage, closeMessage]);

  const showConfirmMessage = useCallback(({ onCustomOk, ...restConfig }: MessageConfirmParams) => {
    updateMessage({
      onCustomOk: async () => {
        if (onCustomOk) {
          await onCustomOk();
        }

        closeMessage();
      },
      ...restConfig,
      show: true,
    });
  }, [updateMessage, closeMessage]);

  const showInfoMessage = React.useCallback((message: string, props?: MessageProps) => {
    showMessage({
      header: I18n.t('Information Message'),
      content: <InfoMessage>{message}</InfoMessage>,
      confirmText: 'Ok',
      onCustomOk: closeMessage,
      ...props,
    });
  }, [showMessage, closeMessage]);

  const showErrorMessage = useCallback((message?: string, size: MessageProps['size'] = 'small') => {
    showMessage({
      header: I18n.t('Operation canceled'),
      content: <ErrorMessage className="Pedigrees__errorMessage">{message}</ErrorMessage>,
      size
    });
  }, [showMessage]);

  return {
    showConfirmMessage,
    showMessage,
    messageProps,
    showInfoMessage,
    showErrorMessage,
    closeMessage
  };
};